/**
 * Unified Status Display System
 * 
 * Provides consistent status display across all components with support for
 * different display variants (badge, dot, etc.) while maintaining the same
 * status logic, colors, and labels.
 * 
 * Based on ConversationDetail.tsx getStatusDisplay() function as the source of truth.
 */

import React from 'react'
import { 
  Clock, 
  UserCheck, 
  User, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Bot, 
  Archive 
} from 'lucide-react'

export type ConversationStatus = 
  | 'pending'
  | 'unresolved'
  | 'assigned'
  | 'accepted'
  | 'escalated'
  | 'completed'
  | 'resolved'
  | 'rejected'
  | 'timeout'
  | 'ai_active'
  | 'human_assigned'
  | 'human_active'
  | 'archived'

export type StatusDisplayVariant = 'badge' | 'dot' | 'icon-only'

export interface StatusDisplayConfig {
  icon: React.ComponentType<{ className?: string }>
  color: string
  label: string
  dotColor: string
  backgroundColor?: string
}

/**
 * Enhanced status mapping for consistency with sidebar
 */
function mapToEnhancedStatus(status: string, hasAssignedAgent: boolean): ConversationStatus {
  // Handle pending status - if assigned agent exists, it's unresolved, otherwise pending
  if (status === 'pending') {
    return hasAssignedAgent ? 'unresolved' : 'pending'
  }
  
  // 🔧 FIX: human_assigned should map to 'pending' to match the UI display
  // This status shows as "Pending" in the UI, so it should filter as pending
  if (status === 'human_assigned' && !hasAssignedAgent) {
    return 'pending';
  }
  
  // If status is assigned, human_assigned, unresolved, or escalated_human, and we have an agent, it's unresolved.
  if ((status === 'assigned' || status === 'human_assigned' || status === 'unresolved' || status === 'escalated_human') && hasAssignedAgent) {
    return 'unresolved';
  }
  
  // Keep ai_active as is to prevent it from being mapped to pending
  if (status === 'ai_active') {
    return 'ai_active'
  }
  
  // Return the status as-is for all other cases
  return status as ConversationStatus
}

/**
 * Get unified status display configuration
 */
export function getUnifiedStatusDisplay(
  status: string, 
  assignedAgentName?: string,
  assignedAgentId?: string
): StatusDisplayConfig {
  // Use the enhanced status mapping for consistency - prefer agent ID over name for accuracy
  const hasAssignedAgent = !!assignedAgentId || !!assignedAgentName
  const enhancedStatus = mapToEnhancedStatus(status, hasAssignedAgent)
  
  switch (enhancedStatus) {
    case 'pending':
      return {
        icon: Clock,
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        label: 'Pending Assignment',
        dotColor: '#FF8C00' // Orange
      }
    
    case 'unresolved':
      return {
        icon: UserCheck,
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        label: `Assigned to ${assignedAgentName || 'Agent'}`,
        dotColor: '#32CD32' // Green
      }
    
    case 'assigned':
      return {
        icon: UserCheck,
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        label: `Assigned to ${assignedAgentName || 'Agent'}`,
        dotColor: '#32CD32' // Green
      }
    
    case 'accepted':
      return {
        icon: User,
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        label: 'Human Active',
        dotColor: '#32CD32' // Green
      }
    
    case 'escalated':
      return {
        icon: AlertTriangle,
        color: 'bg-orange-100 text-orange-800 border-orange-200',
        label: 'Escalated',
        dotColor: '#FF8C00' // Orange
      }
    
    case 'completed':
    case 'resolved':
      return {
        icon: CheckCircle,
        color: 'bg-green-100 text-green-800 border-green-200',
        label: 'Resolved',
        dotColor: '#9370DB' // Purple
      }
    
    case 'rejected':
      return {
        icon: XCircle,
        color: 'bg-red-100 text-red-800 border-red-200',
        label: 'Rejected',
        dotColor: '#DC143C' // Red
      }
    
    case 'timeout':
      return {
        icon: Clock,
        color: 'bg-orange-100 text-orange-800 border-orange-200',
        label: 'Timed Out',
        dotColor: '#FF6347' // Tomato
      }
    
    case 'ai_active':
      return {
        icon: Bot,
        color: 'bg-purple-100 text-purple-800 border-purple-200',
        label: 'AI Active',
        dotColor: '#8A2BE2' // Blue violet
      }
    
    case 'human_assigned':
      return {
        icon: Clock,
        color: 'bg-orange-100 text-orange-800 border-orange-200',
        label: 'Pending',
        dotColor: '#FF8C00' // Orange
      }
    
    case 'human_active':
      return {
        icon: User,
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        label: 'Human Active',
        dotColor: '#32CD32' // Green
      }
    
    case 'archived':
      return {
        icon: Archive,
        color: 'bg-gray-100 text-gray-800 border-gray-200',
        label: 'Archived',
        dotColor: '#808080' // Gray
      }
    
    default:
      return {
        icon: Clock,
        color: 'bg-gray-100 text-gray-800 border-gray-200',
        label: 'Unknown Status',
        dotColor: '#808080' // Gray
      }
  }
}

/**
 * Render status as a badge component
 */
export function StatusBadge({ 
  status, 
  assignedAgentName, 
  assignedAgentId,
  className = '' 
}: {
  status: string
  assignedAgentName?: string
  assignedAgentId?: string
  className?: string
}) {
  const statusDisplay = getUnifiedStatusDisplay(status, assignedAgentName, assignedAgentId)
  const Icon = statusDisplay.icon
  
  return (
    <span className={`font-medium rounded-full border px-2.5 py-1 text-sm inline-flex items-center gap-1 ${statusDisplay.color} ${className}`}>
      <Icon className="w-4 h-4" />
      {statusDisplay.label}
    </span>
  )
}

/**
 * Render status as a dot + text component
 */
export function StatusDot({ 
  status, 
  assignedAgentName, 
  assignedAgentId,
  className = '' 
}: {
  status: string
  assignedAgentName?: string
  assignedAgentId?: string
  className?: string
}) {
  const statusDisplay = getUnifiedStatusDisplay(status, assignedAgentName, assignedAgentId)
  
  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      <div 
        className="w-2 h-2 rounded-full flex-shrink-0"
        style={{ backgroundColor: statusDisplay.dotColor }}
      />
      <span className="text-xs text-gray-600 font-medium">
        {statusDisplay.label}
      </span>
    </div>
  )
}

/**
 * Render status as icon only
 */
export function StatusIcon({ 
  status, 
  assignedAgentName, 
  assignedAgentId,
  className = '' 
}: {
  status: string
  assignedAgentName?: string
  assignedAgentId?: string
  className?: string
}) {
  const statusDisplay = getUnifiedStatusDisplay(status, assignedAgentName, assignedAgentId)
  const Icon = statusDisplay.icon
  
  return (
    <Icon 
      className={`w-4 h-4 ${className}`}
      color={statusDisplay.dotColor}
    />
  )
}

/**
 * Get status display configuration for custom implementations
 */
export function getStatusConfig(status: string, assignedAgentName?: string, assignedAgentId?: string) {
  return getUnifiedStatusDisplay(status, assignedAgentName, assignedAgentId)
}